import React, { useState, useEffect } from 'react';
import PersonalityPresets from './PersonalityPresets';
import TraitSlider from './TraitSlider';
import PersonalityPreview from './PersonalityPreview';
import { presets, traitOptions, traitDescriptions, exampleResponses } from './personalityTraitData';
import { useToast } from './Toast';

// Figma Design System
const designSystem = {
  colors: {
    primary: 'rgb(5, 94, 253)',
    background: 'rgb(255, 255, 255)',
    lightBackground: 'rgb(249, 249, 251)',
    text: 'rgb(34, 35, 36)',
    textSecondary: 'rgb(107, 114, 126)',
    border: 'rgb(229, 231, 235)',
    success: 'rgb(34, 197, 94)',
    warning: 'rgb(245, 158, 11)'
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
    fontWeights: { medium: 500, semibold: 600, bold: 700 }
  },
  borderRadius: '4px',
  spacing: { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px' }
};

const PersonalityTraits = ({ initialTraits = {}, onTraitsChange, selectedVoice = null, businessConfig = {}, hidePreview = false }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [traits, setTraits] = useState({
    formality: initialTraits.formality || 'Strictly Professional',
    verbosity: initialTraits.verbosity || 'Concise',
    empathy: initialTraits.empathy || 'Polite and Neutral',
    proactivity: initialTraits.proactivity || 'Offers Clarifications',
    humor: initialTraits.humor || 'Completely Serious'
  });
  const [showCustom, setShowCustom] = useState(false);
  const [activePreset, setActivePreset] = useState('efficient');
  const { showToast } = useToast();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (initialTraits && Object.keys(initialTraits).length > 0) {
      setTraits(initialTraits);
      checkActivePreset(initialTraits);
    }
  }, [initialTraits]);

  const checkActivePreset = (currentTraits) => {
    for (const [presetKey, presetTraits] of Object.entries(presets)) {
      const matches = Object.keys(presetTraits).every(
        key => currentTraits[key] === presetTraits[key]
      );
      if (matches) {
        setActivePreset(presetKey);
        return;
      }
    }
    setActivePreset(null);
    setShowCustom(true);
  };

  useEffect(() => {
    checkActivePreset(traits);
  }, []);

  const handleTraitChange = async (traitName, value) => {
    const newTraits = { ...traits, [traitName]: value };
    setTraits(newTraits);
    checkActivePreset(newTraits);
    if (onTraitsChange) {
      onTraitsChange(newTraits);
    }
    
    // Auto-save personality traits when slider changes
    try {
      const configService = (await import('../services/configService')).default;
      await configService.updatePersonalityTraits(newTraits);
      showToast('Saved successfully!');
    } catch (error) {
      showToast('Failed to save', 'error');
    }
  };

  const applyPreset = async (presetKey) => {
    const preset = presets[presetKey];
    setTraits(preset);
    setActivePreset(presetKey);
    if (onTraitsChange) onTraitsChange(preset);
    
    try {
      const configService = (await import('../services/configService')).default;
      await configService.updatePersonalityTraits(preset);
      showToast('Saved successfully!');
    } catch (error) {
      showToast('Failed to save', 'error');
    }
  };



  const getSliderValue = (traitName, value) => {
    return traitOptions[traitName].indexOf(value);
  };

  const getValueFromSlider = (traitName, sliderValue) => {
    return traitOptions[traitName][sliderValue];
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: designSystem.spacing.lg }}>
        <h4 style={{
          fontFamily: designSystem.typography.fontFamily,
          fontSize: isMobile ? '16px' : '18px',
          fontWeight: designSystem.typography.fontWeights.semibold,
          color: designSystem.colors.text,
          margin: `0 0 ${designSystem.spacing.sm}`
        }}>Personality Settings</h4>
      </div>

      <PersonalityPresets
        activePreset={activePreset}
        onPresetSelect={applyPreset}
        showCustom={showCustom}
        onToggleCustom={() => setShowCustom(!showCustom)}
        isMobile={isMobile}
      />

      {/* Custom Sliders - Only show when toggled */}
      {showCustom && (
        <div style={{ marginBottom: designSystem.spacing.xl }}>
          <p style={{
            fontFamily: designSystem.typography.fontFamily,
            color: designSystem.colors.textSecondary,
            fontSize: isMobile ? '13px' : '14px',
            marginBottom: designSystem.spacing.lg,
            lineHeight: '1.4'
          }}>
            Fine-tune each personality dimension:
          </p>
          {Object.keys(traitOptions).map((traitName) => (
            <TraitSlider
              key={traitName}
              traitName={traitName}
              value={traits[traitName]}
              options={traitOptions[traitName]}
              description={traitDescriptions[traitName]}
              exampleResponse={exampleResponses[traitName]?.[traits[traitName]]}
              onChange={handleTraitChange}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}

      {!hidePreview && <PersonalityPreview traits={traits} selectedVoice={selectedVoice} businessConfig={businessConfig} />}
    </div>
  );
};

export default PersonalityTraits;